import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdAdd, MdChevronRight, MdChevronLeft, MdFilterList } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { fetchGeneratedIdeasRequest } from '../../redux/slices/generatedIdeasSlice';
import type { RootState } from '../../redux/store';
import FourBigIdeasCarousel from './FourBigIdeasCarousel';
import CreateCampaignDrawer from './CreateCampaignDrawer';
import AllGeneratedIdeasModal from './AllGeneratedIdeasModal';
import UnscheduledCampaignsModal from './UnscheduledCampaignsModal';
import UnscheduledCampaignsCarousel from './UnscheduledCampaignsCarousel';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { useRestaurantNav } from '../../hooks/useRestaurantNav';
import ENDPOINTS from '../../api/endPoints';

interface UnscheduledCampaign {
  id: string;
  title: string;
  description: string;
  type: string;
  validFrom: string;
  validTo: string;
  flyer: any;
  event: any;
  offerDetails: string;
  tagLine: string;
}

const CampaignIdeas: React.FC = () => {
  const dispatch = useDispatch();
  const { navigate } = useRestaurantNav();
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
  const [isAllIdeasModalOpen, setIsAllIdeasModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<string>('Last 2 Weeks');
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [unscheduledCampaigns, setUnscheduledCampaigns] = useState<UnscheduledCampaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [showUnscheduledModal, setShowUnscheduledModal] = useState(false);
  
  // Get restaurant ID and generated ideas from Redux
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);
  const { ideas: generatedIdeas, loading: generatedIdeasLoading, error: generatedIdeasError } = useSelector(
    (state: RootState) => state.generatedIdeas
  );

  // Date filter options (All first, then Last 2 Weeks)
  const dateFilters = ['All', 'Last 2 Weeks'];

  // Filter and sort ideas based on selected date filter
  const filteredIdeas = React.useMemo(() => {
    let filtered = generatedIdeas;
    
    if (selectedFilter === 'Last 2 Weeks') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const twoWeeksAgo = new Date(today);
      twoWeeksAgo.setDate(today.getDate() - 14);

      filtered = generatedIdeas.filter(idea => {
        const eventDate = new Date(idea.trigger_event_date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= twoWeeksAgo;
      });
    }
    
    // Sort by trigger_event_date (most recent/future events first)
    const sorted = [...filtered].sort((a, b) => {
      const dateA = new Date(a.trigger_event_date).getTime();
      const dateB = new Date(b.trigger_event_date).getTime();
      return dateB - dateA; // Most recent event date first
    });
    
    // Debug logging
    console.log('=== Campaign Ideas Sorting by Event Date ===');
    console.log('Total ideas:', sorted.length);
    console.log('First 5 ideas:', sorted.slice(0, 5).map(idea => ({
      id: idea.id,
      event_date: idea.trigger_event_date,
      created_at: idea.created_at,
      title: idea.idea_headline?.substring(0, 50)
    })));
    
    return sorted;
  }, [generatedIdeas, selectedFilter]);

  // Fetch generated ideas on component mount
  useEffect(() => {
    if (!restaurantId) return;
    dispatch(fetchGeneratedIdeasRequest({ restaurantId }));
  }, [dispatch, restaurantId]);

  // Fetch unscheduled campaigns on component mount
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!restaurantId) return;
      
      setLoadingCampaigns(true);
      try {
        const response = await fetch(ENDPOINTS.getAllCampaigns, {
          headers: {
            'restaurant-id': restaurantId,
          },
        });
        
        if (!response.ok) throw new Error('Failed to fetch campaigns');
        
        const result = await response.json();
        console.log('[CampaignIdeas] Campaigns fetched:', result);
        
        if (result.success && result.data) {
          // Sort by createdAt date - most recent first
          const sortedCampaigns = [...result.data].sort((a, b) => {
            const createdAtA = new Date(a.createdAt).getTime();
            const createdAtB = new Date(b.createdAt).getTime();
            return createdAtB - createdAtA; // Most recent first
          });
          
          console.log('[CampaignIdeas] Sorted campaigns (latest first):', sortedCampaigns.slice(0, 3).map(c => ({
            id: c.id,
            title: c.title,
            createdAt: c.createdAt
          })));
          
          setUnscheduledCampaigns(sortedCampaigns);
        }
      } catch (error) {
        console.error('[CampaignIdeas] Error fetching campaigns:', error);
      } finally {
        setLoadingCampaigns(false);
      }
    };
    
    fetchCampaigns();
  }, [restaurantId]);

  // Handle campaign click - navigate immediately, let Playground fetch data
  const handleCampaignClick = (campaignId: string, campaignType: string) => {
    if (!restaurantId) return;
    
    // Navigate immediately - Playground will handle fetching
    navigate(`/${restaurantId}/flyer?campaignId=${campaignId}&campaignType=${campaignType}`);
  };
  
  // Four Big Ideas Data
  const fourBigIdeas = [
    {
            "id": "5ef335a3-dcdf-4a6b-93aa-2c92cac9f610",
            "accountId": "123-01-01T00:00:00",
            "restaurantId": "2001-02-01T00:00:00",
            "eventId": "evt_holi_2026",
            "event": {
                "name": "Holi Festival",
                "date": "2026-03-14T05:30:00",
                "description": "Celebrate Holi with colorful drinks and festive combos."
            },
            "flyer": {
                "image": {
                    "name": "1772799551913-flyer_8.png",
                    "original_name": "flyer_8",
                    "storage_url": "https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1772799551913-flyer_8.png",
                    "generated_count": "0",
                    "llm_suggested_prompt": "",
                    "mode": "IMAGE_ONLY",
                    "properties": {
                        "duration_seconds": null,
                        "width": 1024,
                        "height": 1024,
                        "aspect_ratio": "1:1",
                        "frame_rate": null,
                        "bitrate_kbps": null,
                        "codec": null,
                        "resolution": "Unknown",
                        "orientation": "landscape",
                        "format": "png",
                        "size_bytes": 1733253
                    }
                }
            },
            "eventDate": "2026-03-14T05:30:00",
            "contentId": "",
            "socialMediaId": "",
            "title": "Holi Weekend Special",
            "type": "festival_promo",
            "offerDetails": "Buy 1 Mocktail, Get 1 Free",
            "instructions": "Use bright festive colors and keep text minimal",
            "description": "Celebrate Holi with our colorful drinks and combo platters.",
            "promptId": "",
            "tagLine": "Taste the Colors of Holi!",
            "status": "",
            "flyerId": "",
            "contactInfo": "+1-415-555-0199",
            "validFrom": "2026-03-13T05:30:00",
            "validTo": "2026-03-16T05:30:00",
            "themeColour": "#FF6B35",
            "menuItems": [
                {
                    "items": "Rainbow Mocktail"
                },
                {
                    "items": "Paneer Tikka Platter"
                },
                {
                    "items": "Gulab Jamun Sundae"
                }
            ],
            "refImage": [
                "https://cdn.example.com/images/holi-flyer-1.jpg",
                "https://cdn.example.com/images/holi-flyer-2.jpg"
            ],
            "combo": [
                {
                    "combo": "2 Mocktails + 1 Platter - $19.99"
                },
                {
                    "combo": "Family Combo (4 pax) - $49.99"
                }
            ],
            "createdAt": "2026-03-10T12:57:15",
            "updatedAt": "2026-03-10T12:57:15"
    },
    {
        "id": "5ef335a3-dcdf-4a6b-93aa-2c92cac9f60",
        "accountId": "123-01-01T00:00:00",
        "restaurantId": "2001-02-01T00:00:00",
        "eventId": "evt_holi_2026",
        "event": {
            "name": "Holi Festival",
            "date": "2026-03-14T05:30:00",
            "description": "Celebrate Holi with colorful drinks and festive combos."
        },
        "flyer": {
            "image": {
                "name": "1772799551913-flyer_8.png",
                "original_name": "flyer_8",
                "storage_url": "https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1772799551913-flyer_8.png",
                "generated_count": "0",
                "llm_suggested_prompt": "",
                "mode": "IMAGE_ONLY",
                "properties": {
                    "duration_seconds": null,
                    "width": 1024,
                    "height": 1024,
                    "aspect_ratio": "1:1",
                    "frame_rate": null,
                    "bitrate_kbps": null,
                    "codec": null,
                    "resolution": "Unknown",
                    "orientation": "landscape",
                    "format": "png",
                    "size_bytes": 1733253
                }
            }
        },
        "eventDate": "2026-03-14T05:30:00",
        "contentId": "",
        "socialMediaId": "",
        "title": "Holi Weekend Special",
        "type": "festival_promo",
        "offerDetails": "Buy 1 Mocktail, Get 1 Free",
        "instructions": "Use bright festive colors and keep text minimal",
        "description": "Celebrate Holi with our colorful drinks and combo platters.",
        "promptId": "",
        "tagLine": "Taste the Colors of Holi!",
        "status": "",
        "flyerId": "",
        "contactInfo": "+1-415-555-0199",
        "validFrom": "2026-03-13T05:30:00",
        "validTo": "2026-03-16T05:30:00",
        "themeColour": "#FF6B35",
        "menuItems": [
            {
                "items": "Rainbow Mocktail"
            },
            {
                "items": "Paneer Tikka Platter"
            },
            {
                "items": "Gulab Jamun Sundae"
            }
        ],
        "refImage": [
            "https://cdn.example.com/images/holi-flyer-1.jpg",
            "https://cdn.example.com/images/holi-flyer-2.jpg"
        ],
        "combo": [
            {
                "combo": "2 Mocktails + 1 Platter - $19.99"
            },
            {
                "combo": "Family Combo (4 pax) - $49.99"
            }
        ],
        "createdAt": "2026-03-10T12:57:15",
        "updatedAt": "2026-03-10T12:57:15"
    },
    {
        "id": "5ef335a3-dcdf-4a6b-93aa2c92cac9f610",
        "accountId": "123-01-01T00:00:00",
        "restaurantId": "2001-02-01T00:00:00",
        "eventId": "evt_holi_2026",
        "event": {
            "name": "Holi Festival",
            "date": "2026-03-14T05:30:00",
            "description": "Celebrate Holi with colorful drinks and festive combos."
        },
        "flyer": {
            "image": {
                "name": "1772799551913-flyer_8.png",
                "original_name": "flyer_8",
                "storage_url": "https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1772799551913-flyer_8.png",
                "generated_count": "0",
                "llm_suggested_prompt": "",
                "mode": "IMAGE_ONLY",
                "properties": {
                    "duration_seconds": null,
                    "width": 1024,
                    "height": 1024,
                    "aspect_ratio": "1:1",
                    "frame_rate": null,
                    "bitrate_kbps": null,
                    "codec": null,
                    "resolution": "Unknown",
                    "orientation": "landscape",
                    "format": "png",
                    "size_bytes": 1733253
                }
            }
        },
        "eventDate": "2026-03-14T05:30:00",
        "contentId": "",
        "socialMediaId": "",
        "title": "Holi Weekend Special",
        "type": "festival_promo",
        "offerDetails": "Buy 1 Mocktail, Get 1 Free",
        "instructions": "Use bright festive colors and keep text minimal",
        "description": "Celebrate Holi with our colorful drinks and combo platters.",
        "promptId": "",
        "tagLine": "Taste the Colors of Holi!",
        "status": "",
        "flyerId": "",
        "contactInfo": "+1-415-555-0199",
        "validFrom": "2026-03-13T05:30:00",
        "validTo": "2026-03-16T05:30:00",
        "themeColour": "#FF6B35",
        "menuItems": [
            {
                "items": "Rainbow Mocktail"
            },
            {
                "items": "Paneer Tikka Platter"
            },
            {
                "items": "Gulab Jamun Sundae"
            }
        ],
        "refImage": [
            "https://cdn.example.com/images/holi-flyer-1.jpg",
            "https://cdn.example.com/images/holi-flyer-2.jpg"
        ],
        "combo": [
            {
                "combo": "2 Mocktails + 1 Platter - $19.99"
            },
            {
                "combo": "Family Combo (4 pax) - $49.99"
            }
        ],
        "createdAt": "2026-03-10T12:57:15",
        "updatedAt": "2026-03-10T12:57:15"
    },
    {
        "id": "5ef33a3-dcdf-4a6b-93aa-2c92cac9f610",
        "accountId": "123-01-01T00:00:00",
        "restaurantId": "2001-02-01T00:00:00",
        "eventId": "evt_holi_2026",
        "event": {
            "name": "Holi Festival",
            "date": "2026-03-14T05:30:00",
            "description": "Celebrate Holi with colorful drinks and festive combos."
        },
        "flyer": {
            "image": {
                "name": "1772799551913-flyer_8.png",
                "original_name": "flyer_8",
                "storage_url": "https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1772799551913-flyer_8.png",
                "generated_count": "0",
                "llm_suggested_prompt": "",
                "mode": "IMAGE_ONLY",
                "properties": {
                    "duration_seconds": null,
                    "width": 1024,
                    "height": 1024,
                    "aspect_ratio": "1:1",
                    "frame_rate": null,
                    "bitrate_kbps": null,
                    "codec": null,
                    "resolution": "Unknown",
                    "orientation": "landscape",
                    "format": "png",
                    "size_bytes": 1733253
                }
            }
        },
        "eventDate": "2026-03-14T05:30:00",
        "contentId": "",
        "socialMediaId": "",
        "title": "Holi Weekend Special",
        "type": "festival_promo",
        "offerDetails": "Buy 1 Mocktail, Get 1 Free",
        "instructions": "Use bright festive colors and keep text minimal",
        "description": "Celebrate Holi with our colorful drinks and combo platters.",
        "promptId": "",
        "tagLine": "Taste the Colors of Holi!",
        "status": "",
        "flyerId": "",
        "contactInfo": "+1-415-555-0199",
        "validFrom": "2026-03-13T05:30:00",
        "validTo": "2026-03-16T05:30:00",
        "themeColour": "#FF6B35",
        "menuItems": [
            {
                "items": "Rainbow Mocktail"
            },
            {
                "items": "Paneer Tikka Platter"
            },
            {
                "items": "Gulab Jamun Sundae"
            }
        ],
        "refImage": [
            "https://cdn.example.com/images/holi-flyer-1.jpg",
            "https://cdn.example.com/images/holi-flyer-2.jpg"
        ],
        "combo": [
            {
                "combo": "2 Mocktails + 1 Platter - $19.99"
            },
            {
                "combo": "Family Combo (4 pax) - $49.99"
            }
        ],
        "createdAt": "2026-03-10T12:57:15",
        "updatedAt": "2026-03-10T12:57:15"
    }
  ];

 
  // Weekly Campaign Data
  const weeklyCampaigns = [
    {
      id: 1,
      title: "The Lincoln Center Lover's Encore: A Valentine's Prix-Fixe Experience",
      author: 'Olivia Wilson',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770020177820-generated_flyer.jpeg',
      progress: 75,
      postedOn: ['Facebook', 'Instagram']
    },
    {
      id: 2,
      title: 'The Pre-Show Duet: An LTO Pairing of Regency Elegance & Modern Mood',
      author: 'Jonathan Patterson',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1769855904461-generated_flyer.jpeg',
      progress: 60,
      postedOn: ['Facebook', 'Twitter']
    },
    {
      id: 3,
      title: 'The Pre-Show Duet: An LTO Pairing of Regency Elegance & Modern Mood',
      author: 'Takehiko Kanegi',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'http://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1769855746918-generated_flyer.jpeg',
      progress: 90,
      postedOn: ['Instagram']
    },
    {
      id: 4,
      title: 'The Pre-Show Duet: An LTO Pairing of Regency Elegance & Modern Mood',
      author: 'Takehiko Kanegi',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1770032306070-generated_flyer.jpeg'
    },
    {
      id: 5,
      title: 'The Pre-Show Duet: An LTO Pairing of Regency Elegance & Modern Mood',
      author: 'Jonathan Patterson',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'https://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1769855904461-generated_flyer.jpeg'
    },
    {
      id: 6,
      title: 'The Pre-Show Duet: An LTO Pairing of Regency Elegance & Modern Mood',
      author: 'Takehiko Kanegi',
      rating: 'active',
      description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
      image: 'http://snapmenu-restaurant-workers.raghavan-7fc.workers.dev/files/1769855746918-generated_flyer.jpeg'
    },
  ];

  const scrollContainer = (direction: 'left' | 'right', containerId: string) => {
    const container = document.getElementById(containerId);
    if (container) {
      const scrollAmount = 300;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="p-">
      {/* Create Campaign Drawer */}
      <CreateCampaignDrawer isOpen={isCreateDrawerOpen} onClose={() => setIsCreateDrawerOpen(false)} />

      {/* All Generated Ideas Modal */}
      <AllGeneratedIdeasModal 
        isOpen={isAllIdeasModalOpen} 
        onClose={() => setIsAllIdeasModalOpen(false)}
        ideas={generatedIdeas}
        selectedFilter={selectedFilter}
        onFilterChange={setSelectedFilter}
      />

      {/* Four Big Ideas Section */}
      <div className="mb-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-medium text-[#EE4423]">
            Four big ideas
          </h2>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsCreateDrawerOpen(true)}
            className="px-4 py-2.5 bg-[#EE4423] hover:bg-[#d63d20] text-white rounded-full font-medium text-md flex items-center gap-2 transition-colors"
          >
            <MdAdd size={20} />
            Create Campaign
          </motion.button>
        </div>

        {/* Carousel */}
        <FourBigIdeasCarousel ideas={fourBigIdeas} autoplayInterval={5000} />
      </div>

      {/* Old Create Card - Keeping for reference but hidden */}
      <div className="hidden">
        <div className="flex items-center justify-center">
          <motion.div
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsCreateDrawerOpen(true)}
            className="w-full h-[300px] rounded-2xl bg-gradient-to-br from-[#EE4423] to-[#d63d20] flex flex-col items-center justify-center cursor-pointer relative overflow-hidden"
            style={{
              backdropFilter: 'blur(10px)',
              boxShadow: '0 0 14px 0 #0000001a'
            }}
          >
            {/* Sparkle Icons with Animation */}
            <div className="relative mb-4">
              {/* Large Sparkle - Center */}
              <motion.svg
                width="80"
                height="80"
                viewBox="0 0 24 24"
                fill="none"
                className="text-white"
                animate={{
                  opacity: [0.6, 1, 0.6],
                  scale: [1, 1.1, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <path
                  d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
                  fill="currentColor"
                />
              </motion.svg>
              
              {/* Small Sparkle - Top Right */}
              <motion.svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                className="text-white absolute -top-2 -right-6"
                animate={{
                  opacity: [0.4, 1, 0.4],
                  rotate: [0, 180, 360],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              >
                <path
                  d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
                  fill="currentColor"
                />
              </motion.svg>
              
              {/* Medium Sparkle - Bottom Left */}
              <motion.svg
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="none"
                className="text-white absolute -bottom-3 -left-8"
                animate={{
                  opacity: [0.5, 1, 0.5],
                  scale: [0.9, 1.2, 0.9],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1
                }}
              >
                <path
                  d="M12 2L13.5 8.5L20 10L13.5 11.5L12 18L10.5 11.5L4 10L10.5 8.5L12 2Z"
                  fill="currentColor"
                />
              </motion.svg>
            </div>
            
            <h3 className="text-white text-2xl font-bold">Creat New Campaing</h3>
          </motion.div>
        </div>
      </div>

      {/* Weekly Campaign Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-medium text-[#EE4423] flex items-center gap-2">
            Scheduled Campaingns
            {/* <button className="text-gray-400 text-sm">
              <MdChevronRight size={20} />
            </button> */}
          </h2>
          <button className="text-gray-400 text-sm hover:text-[#EE4423] transition-colors">
            View More
          </button>
        </div>

        {/* Horizontal Scrollable Cards */}
        <div className="relative">
          <button
            onClick={() => scrollContainer('left', 'weekly-scroll')}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
          >
            <MdChevronLeft size={24} className="text-white" />
          </button>

          <div
            id="weekly-scroll"
            className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {weeklyCampaigns.map((campaign) => (
              <motion.div
                key={campaign.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex-shrink-0 w-[280px] rounded-2xl overflow-hidden bg-gray-800/50 border border-gray-700/50"
                style={{
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 0 14px 0 #0000001a'
                }}
              >
                {/* Campaign Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={campaign.image}
                    alt={campaign.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-3 right-3">
                    <span className="px-3 py-1 bg-gray-700/90 text-white text-xs rounded-full">
                      {campaign.rating}
                    </span>
                  </div>
                  
                  {/* Posted On Badges */}
                  {campaign.postedOn && campaign.postedOn.length > 0 && (
                    <div className="absolute bottom-3 left-3 flex gap-2">
                      {campaign.postedOn.map((platform, index) => (
                        <span key={index} className="px-2 py-1 bg-black/70 text-white text-[10px] rounded">
                          {platform}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Campaign Info */}
                <div className="p-4">
                  <h3 className="text-white font-medium text-xs mb-3">{campaign.title}</h3>
                  
                  {/* Progress Bar */}
                  {campaign.progress !== undefined && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        {/* <span className="text-gray-400">Progress</span> */}
                        <span className="text-[#EE4423] font-medium">{campaign.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-1">
                        <div
                          className="bg-gradient-to-r from-[#EE4423] to-[#ff6b4a] h-1 rounded-full transition-all duration-300"
                          style={{ width: `${campaign.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <button
            onClick={() => scrollContainer('right', 'weekly-scroll')}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-gray-800/80 p-2 rounded-full hover:bg-gray-700 transition-colors"
          >
            <MdChevronRight size={24} className="text-white" />
          </button>
        </div>
      </div>

      {/* Unscheduled Campaigns Carousel */}
      <UnscheduledCampaignsCarousel
        campaigns={unscheduledCampaigns}
        loading={loadingCampaigns}
        onCampaignClick={handleCampaignClick}
        onViewMore={() => setShowUnscheduledModal(true)}
      />

      {/* Unscheduled Campaigns Modal */}
      <UnscheduledCampaignsModal
        isOpen={showUnscheduledModal}
        onClose={() => setShowUnscheduledModal(false)}
        campaigns={unscheduledCampaigns}
        loading={loadingCampaigns}
        onCampaignClick={handleCampaignClick}
      />
    </div>
  );
};

export default CampaignIdeas;
